import React, { useState, useEffect } from "react";
import { Stack } from "@chakra-ui/react";
import { useCustomNotification } from "components/Toasts/OpenNotification";
import ReusableDrawer from "components/Drawers/ReusableDrawer";
import headerImage from "../../../../../assets/img/longarrow.jpg";
import GradientButton from "components/Buttons/GradientButton.js";
import ReusableInput from "components/Inputs/ReusableInput.js";
import { formatDate } from "utils/dateFormatter";

const PLATFORM_OPTIONS = [
  { label: "Android", value: "android" },
  { label: "iOS", value: "ios" },
];

const MAINTENANCE_OPTIONS = [
  { label: "Enabled", value: "true" },
  { label: "Disabled", value: "false" },
];

const EditAppVersionDrawer = ({ isOpen, onClose, appVersion, onUpdateVersion }) => {
  const notify = useCustomNotification();

  const [formData, setFormData] = useState({
    version_id: "",
    platform: "android",
    latest_version: "",
    min_supported_version: "",
    store_url: "",
    maintenance_mode: false,
    created_at: "",
  });

  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (appVersion) {
      setFormData({
        version_id: appVersion.version_id || "",
        platform: appVersion.platform || "android",
        latest_version: appVersion.latest_version ?? "",
        min_supported_version: appVersion.min_supported_version ?? "",
        store_url: appVersion.store_url || "",
        maintenance_mode: appVersion.maintenance_mode || false,
        created_at: appVersion.created_at || "",
      });
      setIsEditing(false);
    }
  }, [appVersion]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "latest_version" || name === "min_supported_version"
          ? Number(value)
          : name === "maintenance_mode"
          ? value === "true"
          : value,
    }));
  };

  const handleToggleEdit = async () => {
    if (isEditing) {
      try {
        const response = await onUpdateVersion(formData);

        if (response?.success) {
          notify({
            message: "App Version Updated",
            description: `Version ${formData.latest_version} for ${formData.platform} updated successfully.`,
            type: "success",
          });
          setIsEditing(false);
          onClose();
        } else {
          notify({
            message: "Failed to Update App Version",
            description: response?.error || "An error occurred while updating the app version.",
            type: "error",
          });
        }
      } catch (error) {
        notify({
          message: "Error",
          description: error.message || "Something went wrong while updating the app version.",
          type: "error",
        });
      }
    } else {
      setIsEditing(true);
    }
  };

  return (
    <ReusableDrawer
      isOpen={isOpen}
      onClose={() => {
        setIsEditing(false);
        onClose();
      }}
      headerImage={headerImage}
      headerContent={<h2>{isEditing ? "Edit App Version" : "View App Version"}</h2>}
      bodyContent={
        <Stack spacing={4}>
          <ReusableInput
            label="Platform"
            name="platform"
            value={formData?.platform || "android"}
            onChange={handleInputChange}
            type="select"
            options={PLATFORM_OPTIONS}
            placeholder="Select platform"
            isReadOnly={!isEditing}
          />

          <ReusableInput
            label="Latest Version"
            name="latest_version"
            value={formData?.latest_version || ""}
            onChange={handleInputChange}
            placeholder="Enter latest version"
            type="number"
            isReadOnly={!isEditing}
          />

          <ReusableInput
            label="Minimum Supported Version"
            name="min_supported_version"
            value={formData?.min_supported_version || ""}
            onChange={handleInputChange}
            placeholder="Enter minimum supported version"
            type="number"
            isReadOnly={!isEditing}
          />

          <ReusableInput
            label="Store URL"
            name="store_url"
            value={formData?.store_url || ""}
            onChange={handleInputChange}
            placeholder="Enter app store URL"
            type="text"
            isReadOnly={!isEditing}
          />

          <ReusableInput
            label="Maintenance Mode"
            name="maintenance_mode"
            value={String(formData?.maintenance_mode)}
            onChange={handleInputChange}
            type="select"
            options={MAINTENANCE_OPTIONS}
            placeholder="Select maintenance mode"
            isReadOnly={!isEditing}
          />

          <ReusableInput
            label="Date Added"
            name="created_at"
            value={formatDate(formData?.created_at) || ""}
            isReadOnly
            placeholder="Auto-generated"
          />
        </Stack>
      }
      footerContent={
        <Stack direction="row" spacing={4} justifyContent="flex-end">
          <GradientButton
            onClick={() => {
              setIsEditing(false);
              onClose();
            }}
            fontSize="15px"
            width="100px"
            height="40px"
          >
            Cancel
          </GradientButton>

          <GradientButton
            onClick={handleToggleEdit}
            fontSize="15px"
            width="100px"
            height="40px"
            isDisabled={
              isEditing &&
              (
                !formData.platform ||
                formData.latest_version === "" ||
                formData.min_supported_version === "" ||
                !formData.store_url
              )
            }
          >
            {isEditing ? "Save" : "Edit"}
          </GradientButton>
        </Stack>
      }
    />
  );
};

export default EditAppVersionDrawer;